import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, viewChild } from '@angular/core';
import { TransportOutConfiguration } from '../../models/transport-configuration';
import { Partner, PartnerSystem } from 'src/app/partner/models';
import { TransportType } from '../../models/transport-type';
import { ColumnButtonClickEvent, RowDblClickEvent, SelectionChangedEvent, ToolbarPreparingEvent } from 'devextreme/ui/data_grid';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxiColumnModule, DxoLookupModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';
import { RouterLink } from '@angular/router';
import { StatusTrafficLightComponent } from '../../../shared/status-traffic-light/status-traffic-light.component';
import { BizUserColumnDirective } from '../../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../../shared/grid/biz-grid-date.directive';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
  selector: 'app-transport-out-locations-grid',
  templateUrl: './transport-out-locations-grid.component.html',
  styleUrls: ['./transport-out-locations-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxiColumnModule, DxoLookupModule, DxTemplateModule, RouterLink, StatusTrafficLightComponent, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective]
})
export class TransportOutLocationsGridComponent {
  public bizGridFiltervalues = inject(BizGridFiltervalues);
  datagrid = viewChild.required<DxDataGridComponent>('transports');

  locations = input.required<TransportOutConfiguration[]>();
  partners = input.required<Partner[]>();
  transportTypes = input.required<TransportType[]>();
  partnerSystems = input.required<PartnerSystem[]>();

  singlePartner = input(false);
  allowViewing = input.required<boolean>();
  allowAdding = input.required<boolean>();
  allowCopying = input.required<boolean>();
  allowDeleting = input.required<boolean>();
  allowManageGlobal = input.required<boolean>();
  allowShowLog = input.required<boolean>();

  addLocation = output<void>();
  viewLocation = output<TransportOutConfiguration>();
  copyLocation = output<TransportOutConfiguration>();
  deleteLocation = output<TransportOutConfiguration>();
  selectionChanged = output<TransportOutConfiguration[]>();

  showBlockedColumn = computed(() => this.locations().some(e => e.BlockedByImport));

  constructor() {
    effect(() => {
      this.locations(); // Establish dependency
      this.datagrid().instance?.deselectAll();
    });
  }

  isGlobal = (location: TransportOutConfiguration) => !location.PartnerId;

  onToolbarPreparing = ($event: ToolbarPreparingEvent) => {
    const item = $event.toolbarOptions.items.find(e => e.name === 'addRowButton');
    if (item) {
      item.options.onClick = () => this.emitAddLocation();
    }
  };

  onRowDblClick = ($event: RowDblClickEvent) => {
    if (this.allowViewing()) {
      const location: TransportOutConfiguration = $event.data;
      this.emitViewLocation(location);
    }
  };

  allowManage(location: TransportOutConfiguration): boolean {
    return !!location && (!!location.PartnerId || this.allowManageGlobal());
  }

  checkAllowDeleting = ({ row: { data } }: any): boolean => {
    const location = data as TransportOutConfiguration;
    return this.allowDeleting() && this.allowManage(location);
  };

  checkAllowCopying = ({ row: { data } }: any): boolean => {
    const location = data as TransportOutConfiguration;
    return this.allowCopying() && this.allowManage(location);
  };

  onLocationView = ($event: ColumnButtonClickEvent) => {
    const location: TransportOutConfiguration = $event.row.data;
    this.emitViewLocation(location);
  };

  onLocationCopy = ($event: ColumnButtonClickEvent) => {
    const location: TransportOutConfiguration = $event.row.data;
    this.emitCopyLocation(location);
  };

  onLocationDelete = ($event: ColumnButtonClickEvent) => {
    const location: TransportOutConfiguration = $event.row.data;
    this.emitDeleteLocation(location);
  };

  onSelectionChanged = ($event: SelectionChangedEvent) => {
    const locations = $event.selectedRowsData as TransportOutConfiguration[];
    this.selectionChanged.emit(locations);
  };

  getPartnerName = (cellInfo: any) => {
    const location: TransportOutConfiguration = cellInfo.data;
    return this.partners().find(e => e?.PartnersId === location.PartnerId)?.PartnerName || '';
  };

  emitViewLocation = (location: TransportOutConfiguration) => {
    this.viewLocation.emit(location);
  };

  emitAddLocation = () => {
    this.addLocation.emit();
  };

  emitCopyLocation = (location: TransportOutConfiguration) => {
    this.copyLocation.emit(location);
  };

  emitDeleteLocation = (location: TransportOutConfiguration) => {
    this.deleteLocation.emit(location);
  };
}
